import { useCallback, useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import api from "services/api";

import getQueryParams from "common/get-query-params";
import encodeQueryParams from "common/encode-query-params";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import CONSTANTS from "constants/constant";

import ActionEnum from "enum/action";

import ErrorResponseDto from "dto/services/error-response-dto";
import PageEBrochureDto, { PageEBrochureTableDto, PageEBrochureTableQueryParamsDto, PageEBrochureTableCreatePayloadDto } from "dto/pages/e-brochure/page-e-brochure-dto";

import Button from "components/app-button";
import AppHeading from "components/app-heading";
import AppCategoryTab from "components/app-category-tab";
import EBrochureTable from "components/pages/page-e-brochure/app-e-brochure-table";

const PageEBrochure = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const queryParams = useRef(getQueryParams<PageEBrochureTableQueryParamsDto>(searchParams, { page: 0 }));
	const [header, setHeader] = useState("");
	const [isFetching, setIsFetching] = useState(false);
	const [disableAddNew, setDisableAddNew] = useState(false);
	const [eBrochureData, setEBrochureData] = useState<PageEBrochureDto>();
	const [newRow, setNewRow] = useState<PageEBrochureTableCreatePayloadDto>();
	const [contentTitle, setContentTitle] = useState<PageEBrochureTableDto[]>([]);
	const [categoriesTitleData, setCategoriesTitleData] = useState<PageEBrochureTableDto[]>([]);
	const [selectedContentId, setSelectedContentId] = useState<string>("");
	const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");

	const onHandleGetEBrochure = useCallback(async () => {
		let response = null;

		setIsFetching(true);

		try {
			const { page } = queryParams.current;

			const payload = { size: 10, page };

			const isAllCategory = selectedCategoryId === "all";

			const selectedId = isAllCategory ? selectedContentId : selectedCategoryId;

			if (!selectedId) return;

			response = isAllCategory ? await api.get.brochure.allBrochures(selectedId, payload) : await api.get.brochure.brochures(selectedId, payload);

			setEBrochureData(response?.data?.data?.list);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		} finally {
			setIsFetching(false);
		}
	}, [selectedCategoryId, selectedContentId]);

	const onHandleGetCategoriesTitle = useCallback(async () => {
		try {
			const { page } = queryParams.current;

			const payload = { size: 10, page };

			const response = await api.get.brochure.services(selectedContentId, payload);

			const allCategory = { title: "all", id: "all", status: "active" };

			const dataContent: PageEBrochureTableDto[] = response.data?.data?.list?.content || [];

			const activeDataContent = dataContent.filter((item) => item.status === "active");

			const filterDataContent: PageEBrochureTableDto[] = [allCategory, ...activeDataContent];

			const mapDataContent = filterDataContent.map((m, i) => ({ ...m, indexKey: i }));

			setCategoriesTitleData(mapDataContent);

			setSelectedCategoryId(mapDataContent[0].id);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
	}, [selectedContentId]);

	const onHandleGetContentTitle = useCallback(async () => {
		try {
			const response = await api.get.solution.solutionTitle();

			const dataContent: PageEBrochureTableDto[] = response.data?.data?.list;

			const activeDataContent = dataContent.filter((item) => item.status === "active");

			const mapDataContent = activeDataContent.map((m, i) => ({ ...m, indexKey: i }));

			setContentTitle(mapDataContent);

			setHeader(mapDataContent[0].title);

			setSelectedContentId(mapDataContent[0].id);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
	}, []);

	//prettier-ignore
	const onUpdateBrochure = useCallback(async (payload: PageEBrochureTableDto | string, page?: number) => {
		let response = null;

		try {
			if (typeof payload !== "string") {
				await api.put.brochure.update({ id: payload.id }, payload);
			} else {
				await api.put.brochure.delete({ id: payload });
			}

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);

			if (typeof page === "number") {
				queryParams.current.page = page;
			}

            onHandleGetEBrochure();
		}
	}, [onHandleGetEBrochure]);

	//prettier-ignore
	const onHandleAddBrochure = useCallback(async (payload: PageEBrochureTableCreatePayloadDto) => {
		let response = null;

		try {
			await api.post.brochure.create(payload);

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);

			if (eBrochureData && eBrochureData?.numberOfElements === eBrochureData.size) {
				queryParams.current.page = eBrochureData.totalElements % eBrochureData.size === 0 ? eBrochureData.totalPages : eBrochureData.totalPages - 1;
			}

            onHandleGetEBrochure();
		}
	}, [eBrochureData, onHandleGetEBrochure]);

	//prettier-ignore
	const onHandlePageChange = useCallback((page: number) => {
		queryParams.current.page = page;

		memoSetSearchParams.current(encodeQueryParams(queryParams.current));

        onHandleGetEBrochure();
	}, [onHandleGetEBrochure]);

	//prettier-ignore
	const onHandleSelect = useCallback((tab: any) => {
		const categoryId = tab.id;

		setDisableAddNew(false);

		queryParams.current.page = 0;

		setSelectedCategoryId(categoryId);

		if (newRow) {setNewRow((prev: any) => ({ ...prev, category: categoryId === "all" ? "" : categoryId })); }
	}, [newRow]);

	const onHandleSelectContent = useCallback((tab: any) => {
		const categoryId = tab.id;

		setDisableAddNew(false);

		setHeader(tab.title);

		queryParams.current.page = 0;

		setSelectedContentId(categoryId);

		setNewRow(undefined);
	}, []);

	const onHandleAddNew = () => {
		setDisableAddNew(true);
		setNewRow({ cover: "", document: "", category: selectedCategoryId === "all" ? "" : selectedCategoryId, title: "", displayStatus: "", status: "active" });
	};

	useEffect(() => {
		onHandleGetContentTitle();
	}, [onHandleGetContentTitle]);

	useEffect(() => {
		if (selectedContentId) onHandleGetCategoriesTitle();
	}, [onHandleGetCategoriesTitle, selectedContentId]);

	useEffect(() => {
		if (selectedCategoryId) onHandleGetEBrochure();
	}, [onHandleGetEBrochure, selectedCategoryId]);

	return (
		<div className="page-e-brochure">
			<div className="e-brochure">
				<AppCategoryTab tabs={contentTitle} onSelect={onHandleSelectContent} customBorder />

				<AppHeading title={header}></AppHeading>

				<AppCategoryTab tabs={categoriesTitleData} onSelect={onHandleSelect} />

				<div className="e-brochure__button">
					<Button label="Add New" disabled={disableAddNew} onClick={onHandleAddNew} />
				</div>

				{/* prettier-ignore */}
				<EBrochureTable data={eBrochureData?.content || []} setDisableAddNew={setDisableAddNew} newRow={newRow} setNewRow={setNewRow} onUpdateData={onUpdateBrochure} onCreateData={onHandleAddBrochure} onHandlePageChange={onHandlePageChange} fetching={isFetching} paginationTable={{ currentPage: eBrochureData?.number || 0, totalPages: eBrochureData?.totalPages || 0, totalElements: eBrochureData?.totalElements || 0, numberOfElements: eBrochureData?.numberOfElements || 0, tableSize: eBrochureData?.size || 10 }} id={selectedContentId} selectedCategoryId={selectedCategoryId} />
			</div>
		</div>
	);
};

export default PageEBrochure;
